<script setup>
import { ref, computed, watch } from 'vue';
import { useAlert } from 'dashboard/composables';
import projectDocumentsAPI from 'dashboard/api/nexceo/projectDocuments';
import leadsAPI from 'dashboard/api/nexceo/leads';

// M26.18 (Goal 4/9) — deliberately minimal: lists the project's
// customer_shareable documents (the same set the AI is allowed to share)
// and sends the selected ones through the exact same
// Nexceo::DocumentShareService the AI uses (via leadsAPI.shareDocuments),
// so Timeline/Lead Activity/Kanban/Notification/Memory stay in sync
// automatically — no separate sender, no separate document list.
const props = defineProps({
  isOpen: { type: Boolean, default: false },
  lead: { type: Object, default: null },
  // 'whatsapp' (default, unchanged) sends via leadsAPI.shareDocuments →
  // Nexceo::DocumentShareService (real WhatsApp Meta template on the lead's
  // conversation). 'email' sends via leadsAPI.emailDocuments →
  // Nexceo::LeadDocumentEmailsController (real send via the existing
  // Nexceo::LeadDocumentMailer) — a separate, non-AI-linked path.
  channel: { type: String, default: 'whatsapp' },
});

const emit = defineEmits(['close', 'shared']);

const documents = ref([]);
const isLoadingDocuments = ref(false);
const selectedTypes = ref([]);
const isSending = ref(false);
// M26.19 (Goal 6) — per-document-type Already Shared/Last Delivered/Last Read
// state, keyed by document_type. Purely additive to the existing modal: no
// layout change, just inline status text + a dynamic Send/Resend label.
const shareStatus = ref({});

// M26.19.3 — immutable snapshot of the lead, taken once when the modal opens.
// `props.lead` is a live reference into the parent's reactive lead state
// (Vuex store record on Kanban, a paginated ref on All Leads); a live
// websocket/store refresh while the modal is open can replace or drop that
// object before Send is clicked, which was sending a literal "undefined"
// lead id to the API. Nothing below reads props.lead again after open —
// modalLeadId is always what Send uses, so a lead-object swap mid-modal can
// no longer affect an in-progress share.
const modalLeadId = ref(null);
const modalProjectId = ref(null);
const modalLeadName = ref('');

const shareableDocuments = computed(() =>
  documents.value.filter(
    doc => doc.customer_shareable && doc.active && doc.status === 'active'
  )
);

async function fetchDocuments() {
  if (!modalProjectId.value) {
    documents.value = [];
    return;
  }
  isLoadingDocuments.value = true;
  try {
    const response = await projectDocumentsAPI.get(modalProjectId.value);
    documents.value = response.data || [];
  } catch {
    useAlert('Could not load project documents.');
  } finally {
    isLoadingDocuments.value = false;
  }
}

async function fetchShareStatus() {
  if (!modalLeadId.value) {
    shareStatus.value = {};
    return;
  }
  try {
    const response = await leadsAPI.getDocumentShareStatus(modalLeadId.value);
    shareStatus.value = response.data || {};
  } catch {
    shareStatus.value = {};
  }
}

function statusFor(documentType) {
  return shareStatus.value[documentType] || null;
}

function formatTimestamp(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return '';
  }
}

const sendButtonLabel = computed(() => {
  if (!selectedTypes.value.length) return 'Send';
  return selectedTypes.value.every(type => statusFor(type)) ? 'Resend' : 'Send';
});

watch(
  () => props.isOpen,
  isOpen => {
    if (isOpen) {
      // Snapshot taken here, once — see the modalLeadId/modalProjectId/
      // modalLeadName declaration above for why this must not be re-read
      // from props.lead anywhere below.
      modalLeadId.value = props.lead?.id ?? null;
      modalProjectId.value = props.lead?.project_id ?? null;
      modalLeadName.value = props.lead?.name ?? '';
      selectedTypes.value = [];
      fetchDocuments();
      fetchShareStatus();
    } else {
      modalLeadId.value = null;
      modalProjectId.value = null;
      modalLeadName.value = '';
    }
  }
);

function toggle(documentType) {
  const idx = selectedTypes.value.indexOf(documentType);
  if (idx === -1) {
    selectedTypes.value.push(documentType);
  } else {
    selectedTypes.value.splice(idx, 1);
  }
}

const isEmail = computed(() => props.channel === 'email');
const verb = computed(() => (isEmail.value ? 'emailed' : 'shared'));

async function send() {
  if (!selectedTypes.value.length || isSending.value || !modalLeadId.value) {
    return;
  }
  isSending.value = true;
  try {
    let results;
    if (isEmail.value) {
      const documentIds = documents.value
        .filter(d => selectedTypes.value.includes(d.document_type))
        .map(d => d.id);
      const response = await leadsAPI.emailDocuments(modalLeadId.value, documentIds);
      results = response.data?.results || [];
    } else {
      const response = await leadsAPI.shareDocuments(modalLeadId.value, selectedTypes.value);
      results = response.data?.results || [];
    }
    const sharedCount = results.filter(r => r.status === 'success').length;
    if (sharedCount === results.length) {
      useAlert(
        sharedCount === 1
          ? `Document ${verb.value} with the customer.`
          : `${sharedCount} documents ${verb.value} with the customer.`
      );
    } else if (sharedCount > 0) {
      useAlert(`${sharedCount} of ${results.length} documents ${verb.value}.`);
    } else {
      useAlert(`Could not ${isEmail.value ? 'email' : 'share'} the selected documents.`);
    }
    emit('shared');
    emit('close');
  } catch (err) {
    useAlert(err?.response?.data?.error || `Failed to ${isEmail.value ? 'email' : 'share'} documents.`);
  } finally {
    isSending.value = false;
  }
}
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <teleport to="body">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      @click.self="$emit('close')"
    >
      <div
        class="bg-white dark:bg-n-solid-2 rounded-xl shadow-xl w-full max-w-md mx-4 flex flex-col max-h-[80vh]"
      >
        <div
          class="flex items-center justify-between px-6 py-4 border-b border-n-weak"
        >
          <h2 class="text-base font-semibold text-n-slate-12">
            {{ isEmail ? 'Email Documents' : 'Share Documents' }}
          </h2>
          <button
            class="text-n-slate-10 hover:text-n-slate-12 transition-colors"
            @click="$emit('close')"
          >
            <span class="i-lucide-x size-5" />
          </button>
        </div>

        <div class="overflow-y-auto px-6 py-4 flex flex-col gap-2">
          <p
            v-if="isEmail && !lead?.email"
            class="text-sm text-n-slate-10"
          >
            This lead has no email address on file.
          </p>
          <p v-else-if="isLoadingDocuments" class="text-sm text-n-slate-10">
            Loading documents…
          </p>
          <p
            v-else-if="!shareableDocuments.length"
            class="text-sm text-n-slate-10"
          >
            No customer-shareable documents for this project.
          </p>
          <label
            v-for="doc in (isEmail && !lead?.email ? [] : shareableDocuments)"
            :key="doc.id"
            class="flex items-center gap-2.5 px-2 py-2 rounded-lg hover:bg-n-alpha-1 cursor-pointer"
          >
            <input
              type="checkbox"
              :checked="selectedTypes.includes(doc.document_type)"
              class="rounded"
              @change="toggle(doc.document_type)"
            />
            <span class="text-sm text-n-slate-12">{{ doc.title }}</span>
            <span class="text-xs text-n-slate-9 capitalize">{{
              doc.document_type.replace(/_/g, ' ')
            }}</span>
            <span
              v-if="statusFor(doc.document_type)"
              class="ml-auto flex flex-col items-end text-xs text-n-slate-8 leading-tight"
            >
              <span>Already Shared</span>
              <span v-if="statusFor(doc.document_type).last_read_at">
                Read
                {{ formatTimestamp(statusFor(doc.document_type).last_read_at) }}
              </span>
              <span v-else-if="statusFor(doc.document_type).last_delivered_at">
                Delivered
                {{
                  formatTimestamp(
                    statusFor(doc.document_type).last_delivered_at
                  )
                }}
              </span>
              <span v-else>
                Last sent
                {{
                  formatTimestamp(statusFor(doc.document_type).last_shared_at)
                }}
              </span>
            </span>
          </label>
        </div>

        <div
          class="flex items-center justify-end gap-2 px-6 py-4 border-t border-n-weak"
        >
          <button
            class="text-xs text-n-slate-10 hover:text-n-slate-12 px-3 py-1.5 transition-colors"
            @click="$emit('close')"
          >
            Cancel
          </button>
          <button
            v-if="!isEmail || lead?.email"
            class="flex items-center gap-1.5 text-xs text-white bg-woot-600 hover:bg-woot-700 rounded-lg px-3 py-1.5 transition-colors disabled:opacity-50"
            :disabled="!selectedTypes.length || isSending"
            @click="send"
          >
            <span
              :class="
                isSending ? 'i-lucide-loader-2 animate-spin' : 'i-lucide-send'
              "
              class="size-3.5"
            />
            {{ isSending ? 'Sending…' : sendButtonLabel }}
          </button>
        </div>
      </div>
    </div>
  </teleport>
  <!-- eslint-enable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
</template>
